import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useLeagueSettings } from '../../context/LeagueSettingsContext';
import { useGameContext } from '../../context/GameContext';
import { useCurrentWeek } from '../../context/CurrentWeekContext';
import { useSeason } from '../../context/SeasonContext';
import HiddenText from '../HiddenText';
import { formatDateTime } from '../../utils/timeUtils';
import './LockOverview.css';

const LockOverview = ({ season, leaderboardData }) => {
  const [viewMode, setViewMode] = useState('by-week'); // 'by-week' or 'by-user'
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [lockData, setLockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [allActiveUsers, setAllActiveUsers] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const { user } = useAuth();
  const { settings } = useLeagueSettings();
  const { locksCache, getMaxPicksForWeek } = useGameContext();
  const { currentWeek } = useCurrentWeek();
  const { effectiveSeason } = useSeason();

  // Classic Simpsons prank call names for blurring usernames
  const SIMPSONS_NAMES = [
    "Hugh Jass",
    "Amanda Hugginkiss",
    "Ivana Tinkle",
    "Seymour Butts",
    "Anita Bath",
    "Mike Rotch",
    "I.P. Freely",
    "Ben Dover",
    "Oliver Clothesoff",
    "Homer Sexual"
  ];

  // Function to get a consistent "random" name based on username and week
  const getSimpsonsName = (username, weekNumber) => {
    const seed = username.charCodeAt(0) + weekNumber;
    const index = seed % SIMPSONS_NAMES.length;
    return SIMPSONS_NAMES[index];
  };

  // Check if user has reached max picks for the current week
  const hasReachedMaxPicks = () => {
    if (!currentWeek || !locksCache[currentWeek]) return false;

    const locksForWeek = locksCache[currentWeek];
    const userLocksCount = locksForWeek.filter(lock =>
      lock.userId === user?.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;

    // Get the actual max picks for this week from the backend
    const maxPicksForWeek = getMaxPicksForWeek(currentWeek);

    return userLocksCount >= maxPicksForWeek;
  };

  // Check if user has made a lock for a specific week
  const hasUserLockedForWeek = (weekNumber) => {
    if (!locksCache[weekNumber]) return false;

    const locksForWeek = locksCache[weekNumber];
    const userLocksCount = locksForWeek.filter(lock =>
      lock.userId === user?.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;

    // Get the actual max picks for this week from the backend
    const maxPicksForWeek = getMaxPicksForWeek(weekNumber);

    return userLocksCount >= maxPicksForWeek;
  };



  // Get current week for current season, default to week 1 for past seasons
  const getCurrentWeek = () => {
    // For now, defaulting to week 1 for past seasons
    // This could be enhanced to get the actual current week from the calendar
    return 1;
  };

    // Fetch available weeks for the season
  useEffect(() => {
    const fetchAvailableWeeks = async () => {
      try {
        const response = await api.fetchCalendar(season);
        if (response.data) {
          // Store the full calendar data for the season
          setCalendarData(response.data);

          // Get available weeks (not hidden)
          const weeks = response.data
            .filter(week => !week.hide_week)
            .map(week => week.week)
            .sort((a, b) => a - b);
          setAvailableWeeks(weeks);
        }
      } catch (err) {
        console.error('Error fetching available weeks:', err);
      }
    };

    if (season) {
      fetchAvailableWeeks();
    }
  }, [season]);

  // Fetch all active users
  useEffect(() => {
    const fetchAllActiveUsers = async () => {
      try {
        const response = await api.getActiveUsers();
        if (response.data && Array.isArray(response.data)) {
          setAllActiveUsers(response.data);
        }
      } catch (err) {
        console.error('Error fetching active users:', err);
        setAllActiveUsers([]);
      }
    };

    fetchAllActiveUsers();
  }, []);

    // Initialize selected week and user
  useEffect(() => {
    const currentWeek = getCurrentWeek();
    setSelectedWeek(currentWeek);

    // Fetch all active users for the dropdown
    const fetchActiveUsers = async () => {
      try {
        const response = await api.getActiveUsers();
        if (response.data && Array.isArray(response.data)) {
          setAvailableUsers(response.data);
          if (response.data.length > 0) {
            // Default to current user if available, otherwise first user
            const currentUser = response.data.find(u => u === user?.username);
            setSelectedUser(currentUser || response.data[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching active users:', err);
        // Fallback to leaderboard users if API fails
        if (leaderboardData?.users) {
          setAvailableUsers(leaderboardData.users.map(user => user.username));
          if (leaderboardData.users.length > 0) {
            const currentUser = leaderboardData.users.find(u => u.username === user?.username);
            setSelectedUser(currentUser ? currentUser.username : leaderboardData.users[0].username);
          }
        }
      }
    };

    fetchActiveUsers();
  }, [leaderboardData, user?.username]);

  // Fetch lock overview data
  useEffect(() => {
    const fetchLockData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Only make the API call if we have the required parameters
        if (viewMode === 'by-week' && selectedWeek) {
          const response = await api.getLockOverview(season, selectedWeek, '', viewMode);
          setLockData(response.data);
        } else if (viewMode === 'by-user' && selectedUser) {
          const response = await api.getLockOverview(season, selectedWeek, selectedUser, viewMode);
          setLockData(response.data);
        }
      } catch (err) {
        console.error('Error fetching lock overview data:', err);
        setError('Failed to load lock overview data');
      } finally {
        setLoading(false);
      }
    };

    fetchLockData();
  }, [season, selectedWeek, selectedUser, viewMode]);

  // Function to merge lock data with all active users for By Week view
  const getLocksWithAllUsers = () => {
    if (!lockData || !lockData.locks || !allActiveUsers.length) {
      return lockData?.locks || [];
    }

    // Group lock data by username to handle multiple picks per user (bowl weeks)
    const userNameToLocksMap = new Map();
    lockData.locks.forEach(lock => {
      if (!userNameToLocksMap.has(lock.username)) {
        userNameToLocksMap.set(lock.username, []);
      }
      userNameToLocksMap.get(lock.username).push(lock);
    });

    // For each user, find all their locks and flatten them
    const allActiveUsersArray = Array.isArray(allActiveUsers) ? allActiveUsers : [];
    const usersInSeason = lockData.locks.reduce((acc, lock) => {
      if (!acc.includes(lock.username)) {
        acc.push(lock.username);
      }
      return acc;
    }, []);

    // Start with users who actually made picks this season
    const mergedLocks = [];

    // Add users who have locks in this season
    lockData.locks.forEach(lock => {
      mergedLocks.push(lock);
    });

        // Add "No Pick" entries for users who were active in this season but don't have locks for THIS week
        const usersWithLocksForCurrentWeek = new Set(mergedLocks.map(lock => lock.username));
        const usersActiveInSeason = new Set(usersInSeason);

        allActiveUsersArray.forEach(username => {
          // Only show users if they played in this season but don't have locks for this specific week
          if (!usersWithLocksForCurrentWeek.has(username) && usersActiveInSeason.has(username)) {
            mergedLocks.push({
              username: username,
              kickoff: null,
              matchup: null,
              lock: 'No Pick',
              score: null,
              total: null,
              margin: null,
              status: 'No Pick',
              leadTime: null,
              tv: null
            });
          }
        });

    // State 1: show_locks=true, show_lockers=true - use proper ranking order
    // State 2: show_locks=false, show_lockers=true - use proper ranking order (lockers visible)
    // State 3: show_locks=true, show_lockers=false - use proper ranking order if post-lock, alphabetical if pre-lock
    // State 4: show_locks=false, show_lockers=false - use proper ranking order if post-lock, alphabetical if pre-lock

    const shouldUseProperRanking =
      // State 1: All visible
      (settings.show_locks && settings.show_lockers) ||
      // State 2: Lockers visible
      (!settings.show_locks && settings.show_lockers) ||
      // States 3 & 4: Post-lock (user has made their pick)
      hasReachedMaxPicks() ||
      // Past weeks always use proper ranking
      selectedWeek < currentWeek;

        if (shouldUseProperRanking) {
      // Use proper ranking order - get leaderboard data for ranking
      console.log('LockOverview: shouldUseProperRanking = true');
      console.log('LockOverview: leaderboardData =', leaderboardData);

      if (leaderboardData && leaderboardData.users) {
        console.log('LockOverview: Using leaderboardData.users for ranking');
        // Create a map of users by rank
        const rankMap = new Map();
        leaderboardData.users.forEach(user => {
          rankMap.set(user.username, user.rank);
        });
        console.log('LockOverview: rankMap =', Object.fromEntries(rankMap));

        // Separate users with ranks from users without ranks
        const usersWithRanks = mergedLocks.filter(lock => rankMap.has(lock.username));
        const usersWithoutRanks = mergedLocks.filter(lock => !rankMap.has(lock.username));

        console.log('LockOverview: Users with ranks =', usersWithRanks.map(l => l.username));
        console.log('LockOverview: Users without ranks =', usersWithoutRanks.map(l => l.username));

        // Sort users with ranks by rank, then alphabetically for same rank
        const sortedUsersWithRanks = usersWithRanks.sort((a, b) => {
          const aRank = rankMap.get(a.username);
          const bRank = rankMap.get(b.username);

          console.log(`LockOverview: Comparing ${a.username}(${aRank}) vs ${b.username}(${bRank})`);

          // Convert T1, T3, etc. to numbers for sorting
          let aRankNum = aRank;
          let bRankNum = bRank;

          if (typeof aRank === 'string' && aRank.startsWith('T')) {
            aRankNum = parseInt(aRank.substring(1));
          }
          if (typeof bRank === 'string' && bRank.startsWith('T')) {
            bRankNum = parseInt(bRank.substring(1));
          }

          console.log(`LockOverview: Rank comparison: ${aRankNum} vs ${bRankNum}`);

          if (aRankNum !== bRankNum) {
            return aRankNum - bRankNum;
          }

          // Same rank - sort alphabetically
          console.log(`LockOverview: Same rank, sorting alphabetically: ${a.username} vs ${b.username}`);
          return a.username.localeCompare(b.username);
        });

        // Sort users without ranks alphabetically
        const sortedUsersWithoutRanks = usersWithoutRanks.sort((a, b) =>
          a.username.localeCompare(b.username)
        );

        // Combine: ranked users first, then unranked users
        const finalResult = [...sortedUsersWithRanks, ...sortedUsersWithoutRanks];
        console.log('LockOverview: Final sorted result =', finalResult.map(l => l.username));
        return finalResult;
      }

      // Fallback to alphabetical if no leaderboard data
      console.log('LockOverview: No leaderboard data, using alphabetical fallback');
      return mergedLocks.sort((a, b) => a.username.localeCompare(b.username));
    } else {
      // Use alphabetical order by placeholder names to prevent ranking inference
      console.log('LockOverview: Using alphabetical order by placeholder names');
      const sorted = mergedLocks.sort((a, b) => {
        const aDisplayName = getSimpsonsName(a.username, selectedWeek);
        const bDisplayName = getSimpsonsName(b.username, selectedWeek);
        return aDisplayName.localeCompare(bDisplayName);
      });
      console.log('LockOverview: Alphabetical result =', sorted.map(l => l.username));
      return sorted;
    }
  };

  // Function to handle username click in By Week view
  const handleUsernameClick = (username) => {
    setViewMode('by-user');
    setSelectedUser(username);
  };

  // Function to handle week click in By User view
  const handleWeekClick = (week) => {
    setViewMode('by-week');
    setSelectedWeek(week);
  };

  // Function to check if username should be clickable (not blurred)
  const isUsernameClickable = (lock) => {
    const isCurrentWeek = selectedWeek === currentWeek;
    const isFutureWeek = selectedWeek > currentWeek;
    const hasUserLockedForSelectedWeek = hasUserLockedForWeek(selectedWeek);
    const shouldBlurUser = !hasReachedMaxPicks() && !settings.show_lockers && season === effectiveSeason && (isCurrentWeek || (isFutureWeek && !hasUserLockedForSelectedWeek));
    const shouldBlurEverything = !hasReachedMaxPicks() && !settings.show_locks && !settings.show_lockers && season === effectiveSeason && isCurrentWeek;

    // Username is clickable only when it's visible (not blurred)
    return !shouldBlurUser && !shouldBlurEverything;
  };

  const renderByWeekView = () => {
    if (!lockData) {
      return (
        <div className="no-data-message">
          <p>No lock data available for Week {selectedWeek} of {season}.</p>
        </div>
      );
    }

    // Check if we should show stale data message for States 2 & 4
    // State 2: Hide Locks / Show Lockers - show data but blur usernames, no banner
    // State 4: Hide Locks / Hide Lockers - show stale data message
    const showStaleDataMessage = !hasReachedMaxPicks() && !settings.show_locks && !settings.show_lockers && season === effectiveSeason;
    const previousWeek = currentWeek > 1 ? currentWeek - 1 : 1;

    // Check if all TV values are null to determine if we should show the TV column
    const showTVColumn = lockData?.locks?.some(lock => lock.tv && lock.tv !== '-');

    return (
      <div className="by-week-view">
        {showStaleDataMessage && (
          <div className="stale-data-message">
            <p>
              {currentWeek === 1
                ? "Week 1 data will be available once you complete your lock."
                : `Lock overview reflects data through Week ${previousWeek}. Full data will be available after you complete your lock.`
              }
            </p>
          </div>
        )}
        <div className="week-selector">
          <label htmlFor="week-select">Week:</label>
          <select
            id="week-select"
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
          >
            {availableWeeks.length > 0 ? (
              availableWeeks.map(week => {
                // Get the calendar data to determine if it's a bowl week
                const calendarWeek = calendarData.find(cw => cw.week === week);
                const isBowlWeek = calendarWeek?.season_type === 'postseason' && week >= 17;
                const bowlWeekNumber = isBowlWeek ? week - 16 : null;

                return (
                  <option key={week} value={week}>
                    {isBowlWeek ? `BW${bowlWeekNumber}` : `Week ${week}`}
                  </option>
                );
              })
            ) : (
              <option value={1}>Week 1</option>
            )}
          </select>
        </div>

        <div className="lock-table">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Kickoff</th>
                <th>Matchup</th>
                <th>Lock</th>
                <th>Score</th>
                <th>Total</th>
                <th>Margin</th>
                <th>Status</th>
                <th>Lead Time</th>
                {showTVColumn && <th>TV</th>}
              </tr>
            </thead>
            <tbody>
              {getLocksWithAllUsers().map((lock, index) => {
                // Check if this user has locked in the current week and should be blurred (States 2 & 4)
                const isCurrentWeek = selectedWeek === currentWeek;
                const isFutureWeek = selectedWeek > currentWeek;
                const hasUserLockedForSelectedWeek = hasUserLockedForWeek(selectedWeek);
                const shouldBlurUser = !hasReachedMaxPicks() && !settings.show_lockers && season === effectiveSeason && (isCurrentWeek || (isFutureWeek && !hasUserLockedForSelectedWeek));
                const shouldBlurLockData = !hasReachedMaxPicks() && !settings.show_locks && settings.show_lockers && season === effectiveSeason && isCurrentWeek && lock && lock.lock && lock.lock !== 'No Pick' && lock.lock !== 'Bye Week';

                // State 4: Hide Locks / Hide Lockers - blur everything for current week
                const shouldBlurEverything = !hasReachedMaxPicks() && !settings.show_locks && !settings.show_lockers && season === effectiveSeason && isCurrentWeek;

                return (
                  <tr key={`week-${lock.username}-${index}`}>
                    <td>
                      {shouldBlurEverything ? (
                        <HiddenText
                          text={getSimpsonsName(lock.username, selectedWeek)}
                          tooltip="Nice try! User identity hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : shouldBlurUser ? (
                        <HiddenText
                          text={getSimpsonsName(lock.username, selectedWeek)}
                          tooltip="Nice try! User identity hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : (
                        <span
                          className={isUsernameClickable(lock) ? "clickable-username" : ""}
                          onClick={isUsernameClickable(lock) ? () => handleUsernameClick(lock.username) : undefined}
                          onKeyDown={isUsernameClickable(lock) ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleUsernameClick(lock.username);
                            }
                          } : undefined}
                          tabIndex={isUsernameClickable(lock) ? 0 : -1}
                          role={isUsernameClickable(lock) ? "button" : undefined}
                          aria-label={isUsernameClickable(lock) ? `View ${lock.username}'s lock history` : undefined}
                        >
                          {lock.username}
                        </span>
                      )}
                    </td>
                    <td>
                      {shouldBlurEverything ? (
                        <HiddenText
                          text="Oct 21 2015, 19:28"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : shouldBlurLockData && (lock.lock !== 'Bye Week' && lock.lock !== 'No Pick' && lock.lock !== 'TBD') ? (
                        <HiddenText
                          text="Oct 21 2015, 19:28"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : (
                        (lock.lock === 'Bye Week' || lock.lock === 'No Pick' || lock.lock === 'TBD') ? '-' : formatDateTime(lock.kickoff)
                      )}
                    </td>
                    <td>
                      {shouldBlurEverything ? (
                        <HiddenText
                          text="Jets vs Sharks"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : shouldBlurLockData && (lock.lock !== 'Bye Week' && lock.lock !== 'No Pick' && lock.lock !== 'TBD') ? (
                        <HiddenText
                          text="Jets vs Sharks"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : (
                        (lock.lock === 'Bye Week' || lock.lock === 'No Pick' || lock.lock === 'TBD') ? '-' : lock.matchup
                      )}
                    </td>
                    <td>
                      {shouldBlurEverything ? (
                        <HiddenText
                          text="Jets +5"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : shouldBlurLockData && (lock.lock !== 'Bye Week' && lock.lock !== 'No Pick' && lock.lock !== 'TBD') ? (
                        <HiddenText
                          text="Jets +5"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : (
                        lock.lock
                      )}
                    </td>
                    <td>
                      {shouldBlurEverything ? (
                        <HiddenText
                          text="19-85"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : shouldBlurLockData && (lock.lock !== 'Bye Week' && lock.lock !== 'No Pick' && lock.lock !== 'TBD') ? (
                        <HiddenText
                          text="19-85"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : (
                        (lock.lock === 'Bye Week' || lock.lock === 'No Pick' || lock.lock === 'TBD') ? '-' : (lock.score || '-')
                      )}
                    </td>
                    <td>
                      {shouldBlurEverything ? (
                        <HiddenText
                          text="99"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : shouldBlurLockData && (lock.lock !== 'Bye Week' && lock.lock !== 'No Pick' && lock.lock !== 'TBD') ? (
                        <HiddenText
                          text="99"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : (
                        (lock.lock === 'Bye Week' || lock.lock === 'No Pick' || lock.lock === 'TBD') ? '-' : (lock.total || '-')
                      )}
                    </td>
                    <td>
                      {shouldBlurEverything ? (
                        <HiddenText
                          text="13"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : shouldBlurLockData && (lock.lock !== 'Bye Week' && lock.lock !== 'No Pick' && lock.lock !== 'TBD') ? (
                        <HiddenText
                          text="13"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : (
                        (lock.lock === 'Bye Week' || lock.lock === 'No Pick' || lock.lock === 'TBD') ? '-' : (lock.margin || '-')
                      )}
                    </td>
                    <td className={`status-${lock.status}`}>
                      {shouldBlurEverything ? (
                        <HiddenText
                          text="Unknown"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : shouldBlurLockData && (lock.lock !== 'Bye Week' && lock.lock !== 'No Pick' && lock.lock !== 'TBD') ? (
                        <HiddenText
                          text="Unknown"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : (
                        lock.lock === 'Bye Week' ? '-' : lock.status
                      )}
                    </td>
                    <td>
                      {shouldBlurEverything ? (
                        <HiddenText
                          text="14 D"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : shouldBlurLockData && (lock.lock !== 'Bye Week' && lock.lock !== 'No Pick' && lock.lock !== 'TBD') ? (
                        <HiddenText
                          text="14 D"
                          tooltip="Nice try! Lock details hidden until you make your pick."
                          showTooltip={true}
                        />
                      ) : (
                        (lock.lock === 'Bye Week' || lock.lock === 'No Pick' || lock.lock === 'TBD') ? '-' : lock.leadTime
                      )}
                    </td>
                    {showTVColumn && (
                      <td>
                        {shouldBlurEverything ? (
                          <HiddenText
                            text="ESPN"
                            tooltip="Nice try! Lock details hidden until you make your pick."
                            showTooltip={true}
                          />
                        ) : shouldBlurLockData && (lock.lock !== 'Bye Week' && lock.lock !== 'No Pick' && lock.lock !== 'TBD') ? (
                          <HiddenText
                            text="ESPN"
                            tooltip="Nice try! Lock details hidden until you make your pick."
                            showTooltip={true}
                          />
                        ) : (
                          (lock.lock === 'Bye Week' || lock.lock === 'No Pick' || lock.lock === 'TBD') ? '-' : (lock.tv || '-')
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {lockData.summary && (
            <div className="week-summary">
              <div className="summary-item">
                <span className="label">League Record:</span>
                <span className="value">{lockData.summary.leagueRecord}</span>
              </div>
              <div className="summary-item">
                <span className="label">Total Margin:</span>
                <span className="value">{lockData.summary.totalMargin}</span>
              </div>
              <div className="summary-item">
                <span className="label">Average Margin:</span>
                <span className="value">{lockData.summary.averageMargin}</span>
              </div>
              <div className="summary-item">
                <span className="label">Std Dev Margin:</span>
                <span className="value">{lockData.summary.stdDevMargin}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Function to generate all 21 weeks (15 regular + 6 bowl picks)
  const generateAllWeeks = () => {
    const weeks = [];

    // Regular season weeks (1-15)
    for (let week = 1; week <= 15; week++) {
      weeks.push({
        week: week,
        week_display: `Week ${week}`,
        isBowlWeek: false,
        pickNumber: 1
      });
    }

    // Bowl weeks (17-18, each with 3 picks)
    for (let bowlWeek = 17; bowlWeek <= 18; bowlWeek++) {
      for (let pick = 1; pick <= 3; pick++) {
        weeks.push({
          week: bowlWeek,
          week_display: `BW${bowlWeek - 16}P${pick}`,
          isBowlWeek: true,
          pickNumber: pick
        });
      }
    }

    return weeks;
  };

  // Function to get lock data for a specific week/pick
  const getLockForWeek = (week, pickNumber = 1) => {
    if (!lockData.weeklyLocks) return null;

    return lockData.weeklyLocks.find(lock =>
      lock.week === week &&
      (lock.pickNumber === pickNumber || (!lock.pickNumber && pickNumber === 1))
    );
  };

  const renderByUserView = () => {
    if (!lockData) {
      return (
        <div className="no-data-message">
          <p>No lock data available for {selectedUser} in {season}.</p>
        </div>
      );
    }

    // Check if we should show stale data message for States 2 & 4
    // State 2: Hide Locks / Show Lockers - show data but blur lock details, no banner
    // State 4: Hide Locks / Hide Lockers - show stale data message
    const showStaleDataMessage = !hasReachedMaxPicks() && !settings.show_locks && !settings.show_lockers && season === effectiveSeason;
    const previousWeek = currentWeek > 1 ? currentWeek - 1 : 1;

    // Check if all TV values are null to determine if we should show the TV column
    const showTVColumn = lockData?.weeklyLocks?.some(lock => lock.tv && lock.tv !== '-');

    // Generate all 21 weeks
    const allWeeks = generateAllWeeks();

    return (
      <div className="by-user-view">
        {showStaleDataMessage && (
          <div className="stale-data-message">
            <p>
              {currentWeek === 1
                ? "Week 1 data will be available once you complete your lock."
                : `Lock overview reflects data through Week ${previousWeek}. Full data will be available after you complete your lock.`
              }
            </p>
          </div>
        )}

        {lockData.userProfile && (
          <div className="user-profile">
            <div className="profile-stats">
              <div className="stat-item">
                <span className="label">Record:</span>
                <span className="value">{lockData.userProfile.record}</span>
              </div>
              <div className="stat-item">
                <span className="label">Total Margin:</span>
                <span className="value">{lockData.userProfile.totalMargin}</span>
              </div>
              <div className="stat-item">
                <span className="label">Average Margin:</span>
                <span className="value">{lockData.userProfile.averageMargin}</span>
              </div>
              <div className="stat-item">
                <span className="label">Average Lead Time:</span>
                <span className="value">{lockData.userProfile.averageLeadTime}</span>
              </div>
              <div className="stat-item">
                <span className="label">Current Ranking:</span>
                <span className="value">{lockData.userProfile.ranking}</span>
              </div>
              <div className="stat-item">
                <span className="label">Games Behind:</span>
                <span className="value">{lockData.userProfile.gamesBehind}</span>
              </div>
            </div>

            <div className="pick-habits">
              <div className="habits-grid">
                <div className="habit-item">
                  <span className="label">Favorite:</span>
                  <span className="value">{lockData.userProfile.pickHabits?.favorite || 0}</span>
                </div>
                <div className="habit-item">
                  <span className="label">Underdog:</span>
                  <span className="value">{lockData.userProfile.pickHabits?.underdog || 0}</span>
                </div>
                <div className="habit-item">
                  <span className="label">Over:</span>
                  <span className="value">{lockData.userProfile.pickHabits?.over || 0}</span>
                </div>
                <div className="habit-item">
                  <span className="label">Under:</span>
                  <span className="value">{lockData.userProfile.pickHabits?.under || 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="user-locks-table">
          <table>
            <thead>
              <tr>
                <th>Week</th>
                <th>Kickoff</th>
                <th>Matchup</th>
                <th>Lock</th>
                <th>Score</th>
                <th>Total</th>
                <th>Margin</th>
                <th>Status</th>
                <th>Lead Time</th>
                {showTVColumn && <th>TV</th>}
              </tr>
            </thead>
            <tbody>
              {allWeeks.map((weekInfo, index) => {
                const lock = getLockForWeek(weekInfo.week, weekInfo.pickNumber);

                // Check if this week should be masked (current and future weeks where user hasn't made their lock)
                const isCurrentWeek = weekInfo.week === currentWeek;
                const isFutureWeek = weekInfo.week > currentWeek;
                const shouldMaskRow = !hasUserLockedForWeek(weekInfo.week) && !settings.show_lockers && season === effectiveSeason && (isCurrentWeek || isFutureWeek);

                // Check if lock data should be blurred (State 2: Hide Locks / Show Lockers)
                // Only blur if the user has actually made a lock for this week
                const shouldBlurLockData = !hasReachedMaxPicks() && !settings.show_locks && settings.show_lockers && season === effectiveSeason && isCurrentWeek && lock && lock.lock && lock.lock !== 'No Pick' && lock.lock !== 'Bye Week';

                // Get the appropriate week display for the masking message
                const getWeekDisplayForMessage = (week, pickNumber) => {
                  if (week <= 15) {
                    return `Week ${week}`;
                  } else {
                    const bowlWeekNumber = week - 15;
                    return `Bowl Week ${bowlWeekNumber}, Pick ${pickNumber}`;
                  }
                };

                return (
                  <tr key={`user-${weekInfo.week}-${weekInfo.pickNumber}`} className={shouldMaskRow ? 'masked-row' : ''}>
                    <td>
                      <span
                        className="clickable-week"
                        onClick={() => handleWeekClick(weekInfo.week)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleWeekClick(weekInfo.week);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`View all locks for ${weekInfo.week_display}`}
                      >
                        {weekInfo.week_display}
                      </span>
                    </td>
                    {shouldMaskRow ? (
                      <>
                        <td colSpan={showTVColumn ? 9 : 8} className="masked-content">
                          <div className="mask-overlay">
                            This Lock Data for {selectedUser} will be available after you confirm your lock for {getWeekDisplayForMessage(weekInfo.week, weekInfo.pickNumber)}
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>
                          {shouldBlurLockData && (lock?.lock !== 'Bye Week' && lock?.lock !== 'No Pick') ? (
                            <HiddenText
                              text="Oct 21 2015, 19:28"
                              tooltip="Nice try! Lock details hidden until you make your pick."
                              showTooltip={true}
                            />
                          ) : (
                            (lock?.lock === 'Bye Week' || lock?.lock === 'No Pick' || !lock?.kickoff || formatDateTime(lock?.kickoff) === 'Invalid Date') ? '-' : formatDateTime(lock?.kickoff)
                          )}
                        </td>
                        <td>
                          {shouldBlurLockData && (lock?.lock !== 'Bye Week' && lock?.lock !== 'No Pick') ? (
                            <HiddenText
                              text="Jets vs Sharks"
                              tooltip="Nice try! Lock details hidden until you make your pick."
                              showTooltip={true}
                            />
                          ) : (
                            (lock?.lock === 'Bye Week' || lock?.lock === 'No Pick' || !lock?.matchup) ? '-' : lock?.matchup
                          )}
                        </td>
                        <td>
                          {shouldBlurLockData && (lock?.lock !== 'Bye Week' && lock?.lock !== 'No Pick') ? (
                            <HiddenText
                              text="Jets +5"
                              tooltip="Nice try! Lock details hidden until you make your pick."
                              showTooltip={true}
                            />
                          ) : (
                            lock?.lock || '-'
                          )}
                        </td>
                        <td>
                          {shouldBlurLockData && (lock?.lock !== 'Bye Week' && lock?.lock !== 'No Pick') ? (
                            <HiddenText
                              text="19-85"
                              tooltip="Nice try! Lock details hidden until you make your pick."
                              showTooltip={true}
                            />
                          ) : (
                            (lock?.lock === 'Bye Week' || lock?.lock === 'No Pick') ? '-' : (lock?.score || '-')
                          )}
                        </td>
                        <td>
                          {shouldBlurLockData && (lock?.lock !== 'Bye Week' && lock?.lock !== 'No Pick') ? (
                            <HiddenText
                              text="99"
                              tooltip="Nice try! Lock details hidden until you make your pick."
                              showTooltip={true}
                            />
                          ) : (
                            (lock?.lock === 'Bye Week' || lock?.lock === 'No Pick') ? '-' : (lock?.total || '-')
                          )}
                        </td>
                        <td>
                          {shouldBlurLockData && (lock?.lock !== 'Bye Week' && lock?.lock !== 'No Pick') ? (
                            <HiddenText
                              text="13"
                              tooltip="Nice try! Lock details hidden until you make your pick."
                              showTooltip={true}
                            />
                          ) : (
                            (lock?.lock === 'Bye Week' || lock?.lock === 'No Pick') ? '-' : (lock?.margin || '-')
                          )}
                        </td>
                        <td className={`status-${lock?.status}`}>
                          {shouldBlurLockData && (lock?.lock !== 'Bye Week' && lock?.lock !== 'No Pick') ? (
                            <HiddenText
                              text="Unknown"
                              tooltip="Nice try! Lock details hidden until you make your pick."
                              showTooltip={true}
                            />
                          ) : (
                            lock?.lock === 'Bye Week' ? '-' : (lock?.status || '-')
                          )}
                        </td>
                        <td>
                          {shouldBlurLockData && (lock?.lock !== 'Bye Week' && lock?.lock !== 'No Pick') ? (
                            <HiddenText
                              text="14 D"
                              tooltip="Nice try! Lock details hidden until you make your pick."
                              showTooltip={true}
                            />
                          ) : (
                            (lock?.lock === 'Bye Week' || lock?.lock === 'No Pick') ? '-' : (lock?.leadTime || '-')
                          )}
                        </td>
                        {showTVColumn && (
                          <td>
                            {shouldBlurLockData && (lock?.lock !== 'Bye Week' && lock?.lock !== 'No Pick') ? (
                              <HiddenText
                                text="ESPN"
                                tooltip="Nice try! Lock details hidden until you make your pick."
                                showTooltip={true}
                              />
                            ) : (
                              (lock?.lock === 'Bye Week' || lock?.lock === 'No Pick') ? '-' : (lock?.tv || '-')
                            )}
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="lock-overview-loading">Loading lock overview...</div>;
  }

  if (error) {
    return <div className="lock-overview-error">{error}</div>;
  }

  return (
    <div className="lock-overview">
      <div className="view-mode-selector">
        <button
          className={`mode-button ${viewMode === 'by-week' ? 'active' : ''}`}
          onClick={() => setViewMode('by-week')}
        >
          By Week
        </button>
        <button
          className={`mode-button ${viewMode === 'by-user' ? 'active' : ''}`}
          onClick={() => setViewMode('by-user')}
        >
          By User
        </button>
        {viewMode === 'by-user' && (
          <div className="user-selector-inline">
            <label htmlFor="user-select">User:</label>
            <select
              id="user-select"
              value={selectedUser ?? ''}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              {availableUsers.map(user => (
                <option key={user} value={user}>
                  {user}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {viewMode === 'by-week' ? renderByWeekView() : renderByUserView()}
    </div>
  );
};

LockOverview.propTypes = {
  season: PropTypes.number.isRequired,
  leaderboardData: PropTypes.shape({
    users: PropTypes.arrayOf(PropTypes.shape({
      username: PropTypes.string.isRequired
    }))
  })
};

export default LockOverview;
